import React, { useState, useRef, useEffect } from 'react';
import { Card, Button, Input, Upload, Space, message, Typography, List, Popconfirm, Tag, Select, InputNumber, Dropdown } from 'antd';
import { UploadOutlined, DownloadOutlined, PlusOutlined, DeleteOutlined, EditOutlined, SaveOutlined, FileOutlined, CloudDownloadOutlined } from '@ant-design/icons';
import { PDFDocument, rgb } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';
import theme from '../../theme/globalTheme';
import { getPDFTemplates, downloadPDFTemplate, uploadPDFTemplate } from '../../api/supabase';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const { Text, Title } = Typography;
const { Option } = Select;

function PDFFieldEditor() {
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pdfBytes, setPdfBytes] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [fields, setFields] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.0);
  const [isAddingField, setIsAddingField] = useState(false);
  const [newField, setNewField] = useState({ name: '', x: 0, y: 0, width: 150, height: 20, type: 'text' });
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  // Load templates from Supabase on mount
  useEffect(() => {
    loadTemplatesFromSupabase();
  }, []);

  const loadTemplatesFromSupabase = async () => {
    setLoadingTemplates(true);
    try {
      const { data, error } = await getPDFTemplates();
      if (error) {
        console.error('Error loading templates:', error);
        message.error('Erreur lors du chargement des modèles');
      } else {
        setTemplates(data || []);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      message.error('Erreur lors du chargement des modèles');
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Load template from Supabase
  const loadTemplateFromSupabase = async (filename) => {
    try {
      message.loading({ content: 'Chargement du modèle...', key: 'loadTemplate' });

      const { data: blob, error } = await downloadPDFTemplate(filename);
      if (error) {
        throw error;
      }

      // Convert blob to arrayBuffer
      const arrayBuffer = await blob.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);

      setPdfDoc(pdfDoc);
      setPdfBytes(arrayBuffer);
      setTotalPages(pdfDoc.getPageCount());
      setCurrentPage(1);
      setPdfFile({ name: filename });

      // Create object URL for preview with cache-busting
      // Revoke old URL to free memory
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);

      message.success({ content: `Modèle "${filename}" chargé!`, key: 'loadTemplate' });

      // Extract existing fields if any
      extractExistingFields(pdfDoc);
    } catch (error) {
      console.error('Error loading template from Supabase:', error);
      message.error({ content: 'Erreur lors du chargement du modèle', key: 'loadTemplate' });
    }
  };

  // Load PDF when file is uploaded
  const handleFileUpload = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);

      setPdfDoc(pdfDoc);
      setPdfBytes(arrayBuffer);
      setTotalPages(pdfDoc.getPageCount());
      setCurrentPage(1);
      setPdfFile(file);

      // Create object URL for preview
      const url = URL.createObjectURL(file);
      setPdfUrl(url);

      message.success('PDF chargé avec succès!');

      // Extract existing fields if any
      extractExistingFields(pdfDoc);
    } catch (error) {
      console.error('Error loading PDF:', error);
      message.error('Erreur lors du chargement du PDF');
    }
    return false; // Prevent default upload behavior
  };

  // Extract existing fields from PDF
  const extractExistingFields = (doc) => {
    try {
      const form = doc.getForm();
      const formFields = form.getFields();

      const extractedFields = formFields.map((field, index) => ({
        id: `existing-${index}`,
        name: field.getName(),
        type: 'text',
        isExisting: true
      }));

      setFields(extractedFields);

      if (extractedFields.length > 0) {
        message.info(`${extractedFields.length} champ(s) existant(s) trouvé(s)`);
      }
    } catch (error) {
      console.log('No existing form fields found');
    }
  };

  // Render PDF on canvas
  useEffect(() => {
    if (pdfUrl && canvasRef.current) {
      renderPDF();
    }
  }, [pdfUrl, currentPage, scale]);

  const renderPDF = async () => {
    if (!pdfUrl || !canvasRef.current) return;

    try {
      const loadingTask = pdfjsLib.getDocument(pdfUrl);
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(currentPage);

      const viewport = page.getViewport({ scale });
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      canvas.height = viewport.height;
      canvas.width = viewport.width;

      setCanvasSize({ width: viewport.width, height: viewport.height });

      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };

      await page.render(renderContext).promise;
    } catch (error) {
      console.error('Error rendering PDF:', error);
      message.error('Erreur lors du rendu du PDF');
    }
  };

  // Handle canvas click to add field
  const handleCanvasClick = (e) => {
    if (!isAddingField) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    // Get click position relative to canvas (in display pixels)
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    // Convert to PDF coordinates (unscaled)
    // The canvas is already scaled, so we need to get the actual position
    const x = clickX;
    const y = clickY;

    // Convert to PDF coordinates (PDF origin is bottom-left, canvas origin is top-left)
    const pdfY = canvasSize.height - y;

    setNewField({
      ...newField,
      x: Math.round(x),
      y: Math.round(pdfY)
    });

    message.info(`Position définie: X=${Math.round(x)}, Y=${Math.round(pdfY)}`);
  };

  // Add new field
  const addField = () => {
    if (!newField.name.trim()) {
      message.warning('Veuillez saisir un nom de champ');
      return;
    }

    const field = {
      id: `field-${Date.now()}`,
      name: newField.name,
      x: newField.x,
      y: newField.y,
      width: newField.width,
      height: newField.height,
      type: newField.type,
      page: currentPage,
      isExisting: false
    };

    setFields([...fields, field]);
    setNewField({ name: '', x: 0, y: 0, width: 150, height: 20, type: 'text' });
    setIsAddingField(false);
    message.success('Champ ajouté!');
  };

  // Remove field
  const removeField = (fieldId) => {
    setFields(fields.filter(f => f.id !== fieldId));
    message.success('Champ supprimé');
  };

  // Download PDF with fields
  const downloadPDF = async () => {
    if (!pdfDoc) {
      message.error('Aucun PDF chargé');
      return;
    }

    try {
      // Create a new PDF document from the original
      const newPdfDoc = await PDFDocument.load(pdfBytes);
      const form = newPdfDoc.getForm();

      // Add new fields (skip existing ones)
      const newFields = fields.filter(f => !f.isExisting);

      for (const field of newFields) {
        const page = newPdfDoc.getPage(field.page - 1);
        const { height: pageHeight } = page.getSize();

        // Create text field
        const textField = form.createTextField(field.name);
        textField.addToPage(page, {
          x: field.x,
          y: field.y,
          width: field.width,
          height: field.height,
          borderColor: rgb(0, 0, 0),
          borderWidth: 1,
        });

        textField.enableReadOnly(false);
        textField.enableMultiline(false);
      }

      // Save the PDF
      const pdfBytesOutput = await newPdfDoc.save();

      // Create download link
      const blob = new Blob([pdfBytesOutput], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = pdfFile.name.replace('.pdf', '-with-fields.pdf');
      link.click();

      URL.revokeObjectURL(url);
      message.success('PDF téléchargé avec succès!');
    } catch (error) {
      console.error('Error saving PDF:', error);
      message.error('Erreur lors de la sauvegarde du PDF');
    }
  };

  return (
    <div style={{
      padding: theme.spacing.md,
      maxWidth: '100%',
      minHeight: '100vh',
      background: theme.colors.bgSecondary,
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing.md,
        background: theme.colors.bgPrimary,
        borderRadius: theme.borderRadius.lg,
        boxShadow: theme.effects.shadowMd,
        border: `1px solid ${theme.colors.border}`,
        padding: `${theme.spacing.md} ${theme.spacing.lg}`,
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: theme.borderRadius.md,
            background: theme.colors.primaryLight,
            border: `1px solid ${theme.colors.primary}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            boxShadow: theme.effects.shadowSm,
          }}>
            📝
          </div>
          <div>
            <Title level={4} style={{ margin: 0, color: theme.colors.textPrimary }}>
              Éditeur de Champs PDF
            </Title>
            <Text style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.textSecondary }}>
              Ajoutez des champs remplissables à vos PDF
            </Text>
          </div>
        </div>

        <Space>
          <Dropdown
            menu={{
              items: templates.map(template => ({
                key: template.name,
                label: template.name,
                icon: <FileOutlined />,
                onClick: () => loadTemplateFromSupabase(template.name)
              })),
            }}
            disabled={loadingTemplates || templates.length === 0}
          >
            <Button
              type="default"
              icon={<CloudDownloadOutlined />}
              size="large"
              loading={loadingTemplates}
              style={{
                borderRadius: theme.borderRadius.md,
                boxShadow: theme.effects.shadowSm,
              }}
            >
              Charger depuis Supabase ({templates.length})
            </Button>
          </Dropdown>
          <Upload
            accept=".pdf"
            beforeUpload={handleFileUpload}
            showUploadList={false}
          >
            <Button
              type="default"
              icon={<UploadOutlined />}
              size="large"
              style={{
                borderRadius: theme.borderRadius.md,
                boxShadow: theme.effects.shadowSm,
              }}
            >
              Charger fichier local
            </Button>
          </Upload>
          {pdfFile && (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={downloadPDF}
              size="large"
              style={{
                background: theme.colors.success,
                borderColor: theme.colors.success,
                borderRadius: theme.borderRadius.md,
                boxShadow: theme.effects.shadowMd,
              }}
            >
              Télécharger PDF
            </Button>
          )}
        </Space>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: theme.spacing.md }}>
        {/* PDF Preview */}
        <div>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: theme.borderRadius.sm,
                    background: theme.colors.primaryLight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                  }}>
                    📄
                  </div>
                  <span style={{ color: theme.colors.textPrimary, fontWeight: theme.typography.fontWeight.semibold }}>
                    Aperçu du PDF
                  </span>
                  {pdfFile && (
                    <Tag color={theme.colors.primary}>
                      Page {currentPage} / {totalPages}
                    </Tag>
                  )}
                </div>
                {pdfFile && (
                  <Space>
                    <Button
                      size="small"
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      ← Précédent
                    </Button>
                    <Button
                      size="small"
                      disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      Suivant →
                    </Button>
                    <Select
                      value={scale}
                      onChange={setScale}
                      style={{ width: 100 }}
                      size="small"
                    >
                      <Option value={0.5}>50%</Option>
                      <Option value={0.75}>75%</Option>
                      <Option value={1.0}>100%</Option>
                      <Option value={1.25}>125%</Option>
                      <Option value={1.5}>150%</Option>
                    </Select>
                  </Space>
                )}
              </div>
            }
            style={{
              border: `1px solid ${theme.colors.border}`,
              boxShadow: theme.effects.shadowMd,
            }}
            styles={{ body: { padding: theme.spacing.lg } }}
          >
            {!pdfFile ? (
              <div style={{
                padding: theme.spacing.xxl,
                textAlign: 'center',
                background: 'linear-gradient(135deg, rgba(114, 46, 209, 0.05) 0%, rgba(83, 29, 171, 0.05) 100%)',
                borderRadius: theme.borderRadius.card,
                border: `2px dashed ${theme.colors.primary}`,
                minHeight: '400px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: theme.borderRadius.lg,
                  background: 'linear-gradient(135deg, #722ed1 0%, #531dab 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '32px',
                  marginBottom: theme.spacing.md,
                  boxShadow: '0 4px 12px rgba(114, 46, 209, 0.3)',
                }}>
                  📄
                </div>
                <Text style={{
                  color: theme.colors.textPrimary,
                  fontSize: theme.typography.fontSize.lg,
                  fontWeight: theme.typography.fontWeight.semibold,
                  display: 'block',
                  marginBottom: theme.spacing.sm,
                }}>
                  Chargez un PDF pour commencer
                </Text>
                <Text style={{
                  color: theme.colors.textSecondary,
                  fontSize: theme.typography.fontSize.base,
                }}>
                  Chargez depuis Supabase ou depuis un fichier local
                </Text>
              </div>
            ) : (
              <div
                ref={containerRef}
                style={{
                  position: 'relative',
                  overflow: 'auto',
                  maxHeight: '800px',
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.borderRadius.md,
                  cursor: isAddingField ? 'crosshair' : 'default',
                }}
              >
                <canvas
                  ref={canvasRef}
                  onClick={handleCanvasClick}
                  style={{
                    display: 'block',
                    maxWidth: '100%',
                  }}
                />
              </div>
            )}
          </Card>
        </div>

        {/* Fields Panel */}
        <div>
          {/* Add Field Card */}
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: theme.borderRadius.sm,
                  background: theme.colors.successLight,
                  border: `1px solid ${theme.colors.success}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                }}>
                  ➕
                </div>
                <span style={{ color: theme.colors.textPrimary, fontWeight: theme.typography.fontWeight.semibold }}>
                  Ajouter un champ
                </span>
              </div>
            }
            style={{
              border: `1px solid ${theme.colors.border}`,
              borderLeft: `4px solid ${theme.colors.success}`,
              boxShadow: theme.effects.shadowMd,
              marginBottom: theme.spacing.md,
            }}
            styles={{ body: { padding: theme.spacing.lg } }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <Text strong style={{ display: 'block', marginBottom: theme.spacing.xs }}>
                  Nom du champ
                </Text>
                <Input
                  placeholder="Ex: arrival_date"
                  value={newField.name}
                  onChange={(e) => setNewField({ ...newField, name: e.target.value })}
                  prefix={<EditOutlined style={{ color: theme.colors.textTertiary }} />}
                  size="large"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.sm }}>
                <div>
                  <Text strong style={{ display: 'block', marginBottom: theme.spacing.xs }}>
                    Position X
                  </Text>
                  <InputNumber
                    value={newField.x}
                    onChange={(value) => setNewField({ ...newField, x: value })}
                    style={{ width: '100%' }}
                    size="large"
                  />
                </div>
                <div>
                  <Text strong style={{ display: 'block', marginBottom: theme.spacing.xs }}>
                    Position Y
                  </Text>
                  <InputNumber
                    value={newField.y}
                    onChange={(value) => setNewField({ ...newField, y: value })}
                    style={{ width: '100%' }}
                    size="large"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.sm }}>
                <div>
                  <Text strong style={{ display: 'block', marginBottom: theme.spacing.xs }}>
                    Largeur
                  </Text>
                  <InputNumber
                    value={newField.width}
                    onChange={(value) => setNewField({ ...newField, width: value })}
                    style={{ width: '100%' }}
                    size="large"
                  />
                </div>
                <div>
                  <Text strong style={{ display: 'block', marginBottom: theme.spacing.xs }}>
                    Hauteur
                  </Text>
                  <InputNumber
                    value={newField.height}
                    onChange={(value) => setNewField({ ...newField, height: value })}
                    style={{ width: '100%' }}
                    size="large"
                  />
                </div>
              </div>

              <Button
                type={isAddingField ? 'default' : 'dashed'}
                block
                onClick={() => setIsAddingField(!isAddingField)}
                style={{
                  borderColor: theme.colors.primary,
                  color: theme.colors.primary,
                  fontWeight: theme.typography.fontWeight.medium,
                }}
              >
                {isAddingField ? '❌ Annuler placement' : '🎯 Placer sur le PDF'}
              </Button>

              <Button
                type="primary"
                icon={<PlusOutlined />}
                block
                onClick={addField}
                size="large"
                style={{
                  background: theme.colors.success,
                  borderColor: theme.colors.success,
                  fontWeight: theme.typography.fontWeight.semibold,
                }}
              >
                Ajouter le champ
              </Button>
            </Space>
          </Card>

          {/* Fields List */}
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: theme.borderRadius.sm,
                  background: theme.colors.primaryLight,
                  border: `1px solid ${theme.colors.primary}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                }}>
                  📋
                </div>
                <span style={{ color: theme.colors.textPrimary, fontWeight: theme.typography.fontWeight.semibold }}>
                  Champs ({fields.length})
                </span>
              </div>
            }
            style={{
              border: `1px solid ${theme.colors.border}`,
              borderLeft: `4px solid ${theme.colors.primary}`,
              boxShadow: theme.effects.shadowMd,
            }}
            styles={{ body: { padding: theme.spacing.lg } }}
          >
            {fields.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: theme.spacing.lg,
                color: theme.colors.textSecondary,
              }}>
                <Text type="secondary">Aucun champ ajouté</Text>
              </div>
            ) : (
              <List
                dataSource={fields}
                renderItem={(field) => (
                  <List.Item
                    style={{
                      padding: theme.spacing.sm,
                      background: field.isExisting ? theme.colors.infoLight : theme.colors.bgPrimary,
                      borderRadius: theme.borderRadius.sm,
                      marginBottom: theme.spacing.xs,
                      border: `1px solid ${field.isExisting ? theme.colors.info : theme.colors.border}`,
                    }}
                    actions={[
                      !field.isExisting && (
                        <Popconfirm
                          title="Supprimer ce champ?"
                          onConfirm={() => removeField(field.id)}
                          okText="Oui"
                          cancelText="Non"
                        >
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                          />
                        </Popconfirm>
                      )
                    ]}
                  >
                    <div style={{ flex: 1 }}>
                      <Text strong style={{ display: 'block', color: theme.colors.textPrimary }}>
                        {field.name}
                      </Text>
                      {field.isExisting ? (
                        <Tag color="blue" style={{ marginTop: theme.spacing.xs }}>
                          Existant
                        </Tag>
                      ) : (
                        <Text type="secondary" style={{ fontSize: theme.typography.fontSize.xs }}>
                          Page {field.page} • X: {field.x}, Y: {field.y} • {field.width}x{field.height}
                        </Text>
                      )}
                    </div>
                  </List.Item>
                )}
              />
            )}
          </Card>
        </div>
      </div>

      {/* Instructions */}
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: theme.borderRadius.sm,
              background: theme.colors.warningLight,
              border: `1px solid ${theme.colors.warning}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
            }}>
              💡
            </div>
            <span style={{ color: theme.colors.textPrimary, fontWeight: theme.typography.fontWeight.semibold }}>
              Instructions d'utilisation
            </span>
          </div>
        }
        style={{
          border: `1px solid ${theme.colors.border}`,
          borderLeft: `4px solid ${theme.colors.warning}`,
          boxShadow: theme.effects.shadowMd,
          marginTop: theme.spacing.md,
        }}
        styles={{ body: { padding: theme.spacing.lg } }}
      >
        <div style={{ color: theme.colors.textPrimary }}>
          <ol style={{ paddingLeft: theme.spacing.lg, lineHeight: 1.8 }}>
            <li>
              <strong>Chargez un PDF:</strong>
              <ul style={{ marginTop: theme.spacing.xs }}>
                <li>Cliquez sur "Charger depuis Supabase" pour utiliser un modèle existant</li>
                <li>OU cliquez sur "Charger fichier local" pour téléverser un fichier depuis votre ordinateur</li>
              </ul>
            </li>
            <li>Remplissez le nom du champ (ex: <code>arrival_date</code>, <code>departure_date</code>)</li>
            <li>
              <strong>Option 1:</strong> Entrez manuellement les coordonnées X, Y, largeur et hauteur
              <br />
              <strong>Option 2:</strong> Cliquez sur "Placer sur le PDF" puis cliquez sur le PDF pour définir la position
            </li>
            <li>Ajustez la taille du champ si nécessaire</li>
            <li>Cliquez sur "Ajouter le champ"</li>
            <li>Répétez pour tous les champs nécessaires</li>
            <li>Téléchargez le PDF modifié avec "Télécharger PDF"</li>
          </ol>
          <div style={{
            background: theme.colors.infoLight,
            padding: theme.spacing.md,
            borderRadius: theme.borderRadius.sm,
            marginTop: theme.spacing.md,
            border: `1px solid ${theme.colors.info}`,
          }}>
            <Text strong style={{ color: theme.colors.info }}>
              💡 Astuce:
            </Text>
            <Text style={{ marginLeft: theme.spacing.xs, color: theme.colors.textPrimary }}>
              Les champs existants dans le PDF sont automatiquement détectés et affichés en bleu.
              Vous pouvez ajouter de nouveaux champs qui seront fusionnés avec les existants lors du téléchargement.
              Les modèles sont stockés dans le bucket "templates" de votre Supabase.
            </Text>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default PDFFieldEditor;
